import { ToastWrapper } from "components/shared/Notifications/elements";
import React, { useState, useMemo, useEffect, RefObject, ReactNode } from "react";
import { createPortal } from "react-dom";
import { v4 as uuidv4 } from "uuid";
import { ToastContext, ToastData, ToastDataWithId } from "./ToastContext";
import { Toast } from "./Toast";

type Props = {
    children?: ReactNode;
    financeRef?: RefObject<HTMLDivElement>;
    onCtaClick?: () => void;
    title?: string;
    description?: string;
    canonicalUrl?: string;
    pageName?: string;
    pageType?: string;
    googleAnalyticsId?: string;
    heroImage?: string;
    googleSearchConsole?: string;
    faceBookPixel?: string;
    jumbotronPage?: boolean;
};

export function withToast(Component: React.FC<Props>) {
    return (props: Props): JSX.Element => {
        const [toasts, setToasts] = useState<ToastDataWithId[]>([]);
        const [isDocumentLoaded, setDocumentLoaded] = useState(false);

        const add = ({ title, message, type, duration, positions }: ToastData) => {
            const id = uuidv4();

            setToasts([...toasts, { id, title, message, type, duration, positions }]);
        };

        const remove = (id: string) => setToasts(toasts.filter((t) => t.id !== id));

        const providerValue = useMemo(() => {
            return { add, remove };
        }, [toasts]);

        useEffect(() => {
            setDocumentLoaded(true);
        }, []);

        return (
            <ToastContext.Provider value={providerValue}>
                <Component {...props} />

                {isDocumentLoaded
                    ? createPortal(
                          <ToastWrapper>
                              {toasts.map(({ id, title, message, type, duration, positions }) => (
                                  <Toast
                                      key={id}
                                      remove={() => remove(id)}
                                      title={title}
                                      message={message}
                                      type={type}
                                      duration={duration}
                                      positions={positions}
                                  />
                              ))}
                          </ToastWrapper>,
                          document.body
                      )
                    : null}
            </ToastContext.Provider>
        );
    };
}
